import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { buildPtLinkUrl } from 'homepage/utils/buildPtLinkUrl';
import { FeatureFlags } from 'featureFlags/constants';
import { getYear } from 'shared/utils/getYear';
import { Image } from 'shared/components/Image';
import { variableFontRegular } from 'styles/theme';
import { useFeatureFlag } from 'shared/hooks';
import { useStoreSelector } from 'store/hooks/storeHooks';

import { IConstructorResult } from 'shared/types/ConstructorResult';

interface IProductListTableRow {
  isModuleInViewport?: boolean;
  onClick: () => void;
  product: IConstructorResult;
}

export default function ProductListTableRow({
  isModuleInViewport = false,
  onClick,
  product,
}: IProductListTableRow) {
  const { isDiscoverTabOpen } = useStoreSelector((state) => state.homepage);
  const { value, data } = product;
  const {
    id,
    image_url: imageUrl,
    release_date_year: releaseDateYear,
    season,
    release_date: releaseDate,
  } = data;

  const isDiscoverTabHomepageBackground = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_ADD_DISCOVER_TO_HOMEPAGE_BACKGROUND,
  );
  const linkUrl = buildPtLinkUrl(data);
  const year = getYear(season, releaseDate, releaseDateYear);
  const router = useRouter();

  const handleClick = () => {
    onClick();
    void router.push(linkUrl);
  };

  return (
    <ProductListTableRow.Row key={id} onClick={handleClick}>
      <ProductListTableRow.ImageCell>
        <Link href={linkUrl} passHref prefetch={false}>
          <ProductListTableRow.NameLink>
            <ProductListTableRow.Image
              alt="product_image"
              disableLazyLoad={isModuleInViewport}
              sizes="60px"
              src={
                isDiscoverTabOpen || (isModuleInViewport && isDiscoverTabHomepageBackground)
                  ? imageUrl
                  : 'missing.png'
              }
            />
          </ProductListTableRow.NameLink>
        </Link>
      </ProductListTableRow.ImageCell>
      <ProductListTableRow.NameCell>{value}</ProductListTableRow.NameCell>
      <ProductListTableRow.YearCell>{year}</ProductListTableRow.YearCell>
    </ProductListTableRow.Row>
  );
}

ProductListTableRow.Row = styled.tr`
  display: inline-block;
  border-bottom: ${({ theme }) => theme.borders.white};
  min-width: 310px;
  width: 100%;

  &:hover {
    cursor: pointer;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    min-width: 308px;
  }
`;

ProductListTableRow.DataContainer = styled.td`
  display: inline-block;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  text-align: left;
  padding: 16px 0px;
  width: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 80px;
  }
`;

ProductListTableRow.ImageCell = styled(ProductListTableRow.DataContainer)`
  width: 84px;
  padding-right: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 124px !important;
  }
`;

ProductListTableRow.NameCell = styled(ProductListTableRow.DataContainer)`
  width: 190px;
  padding-right: 24px;
  min-height: 54px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 158px;
  }
`;

ProductListTableRow.YearCell = styled(ProductListTableRow.DataContainer)`
  width: 32px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 22px;
    letter-spacing: -0.5px;
  }
`;

ProductListTableRow.Image = styled(Image)`
  height: 82px;
  width: 82px !important;
  min-width: 82px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: 90px;
    min-width: 90px;
    width: 90px !important;
  }
`;

ProductListTableRow.NameLink = styled.a`
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
